/**
 * GlobalCriteriaEditor
 * Admin section for managing the global criteria definitions (SYSTEM_GLOBAL).
 */

import { useState, useEffect, useCallback } from 'react';
import { AdminService } from '../services/AdminService';
import { ToastManager } from './ToastContainer';
import type { GlobalCriterion, Category } from '../types';

interface GlobalCriteriaEditorProps {
  adminService: AdminService;
}

export function GlobalCriteriaEditor({ adminService }: GlobalCriteriaEditorProps) {
  const [criteria, setCriteria] = useState<GlobalCriterion[]>([]);
  const [originalCriteria, setOriginalCriteria] = useState<GlobalCriterion[]>([]);
  const [version, setVersion] = useState(0);
  const [updatedAt, setUpdatedAt] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showRawEditor, setShowRawEditor] = useState(false);
  const [rawJson, setRawJson] = useState('');
  const [rawJsonError, setRawJsonError] = useState('');

  const toast = ToastManager.getInstance();
  const hasChanges = JSON.stringify(criteria) !== JSON.stringify(originalCriteria);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await adminService.getGlobalCriteria();
      setCriteria(data.criteria);
      setOriginalCriteria(data.criteria);
      setVersion(data.version);
      setUpdatedAt(data.updated_at);
    } catch (err) {
      toast.error('Failed to load global criteria: ' + (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [adminService]);

  useEffect(() => { load(); }, [load]);

  const handlePublish = async () => {
    setIsSaving(true);
    setShowPublishConfirm(false);
    try {
      const result = await adminService.updateGlobalCriteria(criteria);
      setVersion(result.version);
      setOriginalCriteria(criteria);
      setUpdatedAt(new Date().toISOString());
      toast.success(`Global criteria published (v${result.version}, ${result.criteria_count} criteria)`);
    } catch (err) {
      toast.error('Failed to publish: ' + (err as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setCriteria(originalCriteria);
    setExpandedId(null);
  };

  const handleOpenRawEditor = () => {
    setRawJson(JSON.stringify(criteria, null, 2));
    setRawJsonError('');
    setShowRawEditor(true);
  };

  const handleApplyRawJson = () => {
    try {
      const parsed = JSON.parse(rawJson);
      if (!Array.isArray(parsed)) {
        setRawJsonError('Top-level value must be an array.');
        return;
      }
      for (let i = 0; i < parsed.length; i++) {
        const item = parsed[i];
        if (typeof item !== 'object' || item === null || Array.isArray(item)) {
          setRawJsonError(`Item at index ${i} must be an object.`);
          return;
        }
        if (typeof item.id !== 'string' || !item.id) {
          setRawJsonError(`Item at index ${i} is missing a valid "id" (string).`);
          return;
        }
        if (typeof item.name !== 'string' || !item.name) {
          setRawJsonError(`Item at index ${i} is missing a valid "name" (string).`);
          return;
        }
        if (typeof item.description !== 'string') {
          setRawJsonError(`Item at index ${i} is missing a valid "description" (string).`);
          return;
        }
        if (typeof item.default_active !== 'boolean') {
          setRawJsonError(`Item at index ${i}: "default_active" must be a boolean.`);
          return;
        }
        if (!Array.isArray(item.categories) || item.categories.length === 0) {
          setRawJsonError(`Item at index ${i}: "categories" must be a non-empty array.`);
          return;
        }
      }
      setCriteria(parsed as GlobalCriterion[]);
      setShowRawEditor(false);
    } catch (e) {
      setRawJsonError('Invalid JSON: ' + (e as Error).message);
    }
  };

  // ── Criterion-level operations ────────────────────────────────

  const updateCriterion = (id: string, patch: Partial<GlobalCriterion>) => {
    setCriteria(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
  };

  const removeCriterion = (id: string) => {
    if (!window.confirm('Remove this criterion from the global set?')) return;
    setCriteria(prev => prev.filter(c => c.id !== id));
    if (expandedId === id) setExpandedId(null);
  };

  const addCriterion = () => {
    const newId = `criterion-${Date.now()}`;
    const newCriterion: GlobalCriterion = {
      id: newId,
      name: 'New Criterion',
      description: 'Description',
      default_active: false,
      categories: [
        { name: 'Category A', description: 'First category', price_min: 0, price_max: 0 },
        { name: 'Category B', description: 'Second category', price_min: 0, price_max: 0 },
      ],
    };
    setCriteria(prev => [...prev, newCriterion]);
    setExpandedId(newId);
  };

  // ── Category-level operations ─────────────────────────────────

  const updateCategory = (criterionId: string, catIndex: number, patch: Partial<Category>) => {
    setCriteria(prev => prev.map(c => {
      if (c.id !== criterionId) return c;
      const cats = [...c.categories];
      cats[catIndex] = { ...cats[catIndex], ...patch };
      return { ...c, categories: cats };
    }));
  };

  const removeCategory = (criterionId: string, catIndex: number) => {
    setCriteria(prev => prev.map(c => {
      if (c.id !== criterionId) return c;
      const cats = c.categories.filter((_, i) => i !== catIndex);
      return { ...c, categories: cats };
    }));
  };

  const addCategory = (criterionId: string) => {
    setCriteria(prev => prev.map(c => {
      if (c.id !== criterionId) return c;
      return {
        ...c,
        categories: [
          ...c.categories,
          { name: 'New Category', description: 'Description', price_min: 0, price_max: 0 },
        ],
      };
    }));
  };

  // ── Format helpers ────────────────────────────────────────────

  const formatDate = (iso: string) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  // ── Render ────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="gc-editor">
        <h2 className="admin-section-title">Global Criteria</h2>
        <div className="admin-skeleton-rows">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="admin-skeleton-row" style={{ height: '56px' }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="gc-editor">
      {/* Header */}
      <div className="gc-header">
        <div className="gc-header-left">
          <h2 className="admin-section-title" style={{ marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
            Global Criteria
          </h2>
          <span className="gc-version-badge">v{version}</span>
          {updatedAt && (
            <span className="gc-updated-at">Updated {formatDate(updatedAt)}</span>
          )}
        </div>
        <div className="gc-header-actions">
          <button className="admin-btn" onClick={handleOpenRawEditor} disabled={isSaving}>
            Raw Edit
          </button>
          {hasChanges && (
            <button className="admin-btn" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </button>
          )}
          <button
            className="admin-btn admin-btn-primary"
            onClick={() => setShowPublishConfirm(true)}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? 'Publishing...' : 'Publish Changes'}
          </button>
        </div>
      </div>

      {hasChanges && (
        <div className="gc-unsaved-banner">Unsaved changes</div>
      )}

      {/* Criterion list */}
      <div className="gc-criterion-list">
        {criteria.map((criterion) => {
          const isExpanded = expandedId === criterion.id;
          return (
            <div key={criterion.id} className={`gc-criterion-card ${isExpanded ? 'gc-expanded' : ''}`}>
              {/* Collapsed header */}
              <div className="gc-criterion-header" onClick={() => setExpandedId(isExpanded ? null : criterion.id)}>
                <div className="gc-criterion-summary">
                  <span className="gc-criterion-name">{criterion.name}</span>
                  <span className="gc-criterion-desc-snippet">
                    {criterion.description.length > 60
                      ? criterion.description.slice(0, 60) + '...'
                      : criterion.description}
                  </span>
                </div>
                <div className="gc-criterion-meta">
                  <span className="gc-cat-count">{criterion.categories.length} categories</span>
                  <span className={`gc-default-badge ${criterion.default_active ? 'gc-on' : 'gc-off'}`}>
                    {criterion.default_active ? 'ON' : 'OFF'}
                  </span>
                  <span className="gc-expand-arrow">{isExpanded ? '\u25B2' : '\u25BC'}</span>
                </div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="gc-criterion-body">
                  <div className="gc-field-row">
                    <label className="admin-field-label">
                      Criterion ID
                      <input
                        className="admin-input gc-input-full"
                        value={criterion.id}
                        onChange={(e) => updateCriterion(criterion.id, { id: e.target.value })}
                      />
                    </label>
                  </div>
                  <div className="gc-field-row">
                    <label className="admin-field-label">
                      Name
                      <input
                        className="admin-input gc-input-full"
                        value={criterion.name}
                        onChange={(e) => updateCriterion(criterion.id, { name: e.target.value })}
                      />
                    </label>
                  </div>
                  <div className="gc-field-row">
                    <label className="admin-field-label">
                      Description
                      <textarea
                        className="admin-input gc-textarea"
                        value={criterion.description}
                        onChange={(e) => updateCriterion(criterion.id, { description: e.target.value })}
                        rows={2}
                      />
                    </label>
                  </div>
                  <div className="gc-field-row gc-toggle-row">
                    <label className="gc-toggle-label">
                      <input
                        type="checkbox"
                        checked={criterion.default_active}
                        onChange={(e) => updateCriterion(criterion.id, { default_active: e.target.checked })}
                      />
                      <span>Default Active (new users get this ON)</span>
                    </label>
                  </div>

                  {/* Categories table */}
                  <div className="gc-categories-section">
                    <h4 className="gc-categories-title">Categories</h4>
                    <div className="gc-cat-table-wrapper">
                      <table className="gc-cat-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Description</th>
                            <th>Min Price</th>
                            <th>Max Price</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {criterion.categories.map((cat, catIdx) => (
                            <tr key={catIdx}>
                              <td>
                                <input
                                  className="admin-input gc-cat-input"
                                  value={cat.name}
                                  onChange={(e) => updateCategory(criterion.id, catIdx, { name: e.target.value })}
                                />
                              </td>
                              <td>
                                <input
                                  className="admin-input gc-cat-input gc-cat-desc-input"
                                  value={cat.description}
                                  onChange={(e) => updateCategory(criterion.id, catIdx, { description: e.target.value })}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="admin-input gc-cat-input gc-cat-price-input"
                                  value={cat.price_min}
                                  min={0}
                                  step={0.5}
                                  onChange={(e) => updateCategory(criterion.id, catIdx, { price_min: parseFloat(e.target.value) || 0 })}
                                />
                              </td>
                              <td>
                                <input
                                  type="number"
                                  className="admin-input gc-cat-input gc-cat-price-input"
                                  value={cat.price_max}
                                  min={0}
                                  step={0.5}
                                  onChange={(e) => updateCategory(criterion.id, catIdx, { price_max: parseFloat(e.target.value) || 0 })}
                                />
                              </td>
                              <td>
                                <button
                                  className="admin-btn admin-btn-sm admin-btn-danger"
                                  onClick={() => removeCategory(criterion.id, catIdx)}
                                  disabled={criterion.categories.length <= 1}
                                  title="Remove category"
                                >
                                  X
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button
                      className="admin-btn admin-btn-sm gc-add-cat-btn"
                      onClick={() => addCategory(criterion.id)}
                    >
                      + Add Category
                    </button>
                  </div>

                  <div className="gc-criterion-actions">
                    <button
                      className="admin-btn admin-btn-danger"
                      onClick={() => removeCriterion(criterion.id)}
                    >
                      Remove Criterion
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button className="admin-btn gc-add-criterion-btn" onClick={addCriterion}>
        + Add New Criterion
      </button>

      {/* Publish confirmation modal */}
      {showPublishConfirm && (
        <div className="admin-modal-backdrop" onClick={() => setShowPublishConfirm(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-modal-title">Publish Global Criteria?</h3>
            <p className="admin-modal-subtitle">
              This will update the global criteria for all users. Users will see the
              changes on their next request. Custom user prices and toggles are preserved.
            </p>
            <p className="gc-publish-summary">
              {criteria.length} criteria, version will be v{version + 1}
            </p>
            <div className="admin-modal-actions">
              <button className="admin-btn" onClick={() => setShowPublishConfirm(false)}>
                Cancel
              </button>
              <button className="admin-btn admin-btn-primary" onClick={handlePublish}>
                Publish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Raw JSON editor modal */}
      {showRawEditor && (
        <div className="admin-modal-backdrop" onClick={() => setShowRawEditor(false)}>
          <div className="admin-modal gc-raw-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-modal-title">Raw JSON Editor</h3>
            <textarea
              className="gc-raw-textarea"
              value={rawJson}
              onChange={(e) => {
                setRawJson(e.target.value);
                setRawJsonError('');
              }}
              spellCheck={false}
            />
            {rawJsonError && (
              <div className="gc-raw-error">{rawJsonError}</div>
            )}
            <div className="admin-modal-actions">
              <button className="admin-btn" onClick={() => setShowRawEditor(false)}>
                Cancel
              </button>
              <button className="admin-btn admin-btn-primary" onClick={handleApplyRawJson}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
